/**
 * @fileoverview Sudoku Board Component
 * @description Renders the 9x9 Sudoku grid with all cells
 */

import React, { memo, useMemo } from "react";
import { View, useWindowDimensions, useColorScheme } from "react-native";
import { SudokuCell } from "./SudokuCell";
import { SudokuGameState } from "../types";
import {
  getRelatedIndices,
  getCellsWithValue,
} from "../utils/sudokuValidator";

interface SudokuBoardProps {
  /** Current game state */
  gameState: SudokuGameState;
  /** Called when a cell is pressed */
  onCellPress: (index: number) => void;
  /** Whether interactions are disabled */
  disabled?: boolean;
  /** Show mistakes immediately */
  showMistakes?: boolean;
}

/**
 * The main 9x9 Sudoku board
 */
export const SudokuBoard = memo<SudokuBoardProps>(
  ({ gameState, onCellPress, disabled = false, showMistakes = true }) => {
    const { width: screenWidth } = useWindowDimensions();
    const colorScheme = useColorScheme();
    const isDark = colorScheme === "dark";

    // Calculate board size (leave some padding)
    const boardSize = Math.min(screenWidth - 32, 360);
    const cellSize = Math.floor(boardSize / 9);
    const actualBoardSize = cellSize * 9;

    // Colors
    const bgColor = isDark ? "#111827" : "#FFFFFF";
    const thickBorderColor = isDark ? "#4B5563" : "#1F2937";
    const thinBorderColor = isDark ? "#374151" : "#D1D5DB";

    // Memoize highlighted cells
    const highlightedCells = useMemo(() => {
      if (gameState.selected_cell === null) return new Set<number>();
      return new Set(getRelatedIndices(gameState.selected_cell));
    }, [gameState.selected_cell]);

    // Memoize same number cells
    const sameNumberCells = useMemo(() => {
      if (gameState.selected_cell === null) return new Set<number>();
      const selectedValue = gameState.current[gameState.selected_cell];
      if (selectedValue === "0") return new Set<number>();
      return new Set(getCellsWithValue(gameState.current, parseInt(selectedValue, 10)));
    }, [gameState.selected_cell, gameState.current]);

    // Render all cells organized in rows
    const rows = useMemo(() => {
      const rowElements = [];

      for (let row = 0; row < 9; row++) {
        const cellsInRow = [];

        for (let col = 0; col < 9; col++) {
          const index = row * 9 + col;
          const puzzleChar = gameState.puzzle[index];
          const currentChar = gameState.current[index];
          const solutionChar = gameState.solution[index];

          const value = currentChar === "0" ? null : parseInt(currentChar, 10);
          const isGiven = puzzleChar !== "0";
          const isSelected = gameState.selected_cell === index;
          const isHighlighted = highlightedCells.has(index);
          const isSameNumber = sameNumberCells.has(index);

          const isError =
            showMistakes &&
            value !== null &&
            !isGiven &&
            currentChar !== solutionChar;

          const notes = (gameState.notes ?? {})[index] || [];

          // Determine border styles based on position
          const isRightBoxEdge = col === 2 || col === 5;
          const isBottomBoxEdge = row === 2 || row === 5;
          const isLastCol = col === 8;
          const isLastRow = row === 8;

          cellsInRow.push(
            <View
              key={index}
              style={{
                width: cellSize,
                height: cellSize,
                borderRightWidth: isLastCol ? 0 : (isRightBoxEdge ? 2 : 1),
                borderBottomWidth: isLastRow ? 0 : (isBottomBoxEdge ? 2 : 1),
                borderRightColor: isRightBoxEdge ? thickBorderColor : thinBorderColor,
                borderBottomColor: isBottomBoxEdge ? thickBorderColor : thinBorderColor,
              }}
            >
              <SudokuCell
                value={value}
                isGiven={isGiven}
                isSelected={isSelected}
                isHighlighted={isHighlighted}
                isSameNumber={isSameNumber}
                isError={isError}
                isHint={false}
                notes={notes}
                index={index}
                cellSize={cellSize}
                onPress={() => onCellPress(index)}
                disabled={disabled}
              />
            </View>
          );
        }

        rowElements.push(
          <View
            key={`row-${row}`}
            style={{
              flexDirection: "row",
            }}
          >
            {cellsInRow}
          </View>
        );
      }

      return rowElements;
    }, [
      gameState.puzzle,
      gameState.current,
      gameState.solution,
      gameState.selected_cell,
      gameState.notes,
      highlightedCells,
      sameNumberCells,
      cellSize,
      showMistakes,
      disabled,
      onCellPress,
      thickBorderColor,
      thinBorderColor,
    ]);

    return (
      <View
        style={{
          width: actualBoardSize,
          height: actualBoardSize,
          borderWidth: 2,
          borderColor: thickBorderColor,
          borderRadius: 4,
          backgroundColor: bgColor,
          overflow: "hidden",
        }}
        accessible={true}
        accessibilityLabel="Sudoku bord"
      >
        {rows}
      </View>
    );
  }
);

SudokuBoard.displayName = "SudokuBoard";
